import type { DesignLabCtaEventName, Side } from "@design-lab/shared/types";
import { track, trackRudderstack } from "./trackEvent";

export type DesignLabSizeTier = "XS-XL" | "2XL-3XL" | "4XL-5XL";

export type DesignLabPricingChangeReason = "qty_changed" | "size_changed" | "color_changed";

// Returns the largest size tier in the cart (4XL-5XL > 2XL-3XL > XS-XL); sizes outside
// these tiers (e.g. 6XL) return undefined and callers omit `tier` from the event.
export const deriveSizeTier = (sizes: string[]): DesignLabSizeTier | undefined => {
  if (sizes.length === 0) return undefined;
  const normalized = sizes.map(s => s.trim().toUpperCase());
  if (normalized.some(s => s === "4XL" || s === "5XL")) return "4XL-5XL";
  if (normalized.some(s => s === "2XL" || s === "3XL")) return "2XL-3XL";
  if (normalized.some(s => ["XS", "S", "M", "L", "XL"].includes(s))) return "XS-XL";
  return undefined;
};

export const trackChatCtaClicked = () => {
  track("design_lab_chat_cta_clicked", {
    timestamp: Date.now()
  });
};

export const trackLearnMoreClicked = () => {
  track("design_lab_learn_more_clicked", {
    timestamp: Date.now()
  });
};

export const trackTabNavigation = (tabName: string, source: "tabs" | "next") => {
  track("design_lab_tab_clicked", {
    tab_name: tabName,
    location: "design_lab",
    source: source === "tabs" ? "tab_click" : "next_click",
    timestamp: Date.now()
  });
};

export const trackUploadTabNavigation = (tabName: "size" | "placement" | "image_enhancement") => {
  track("design_lab_upload_tab_clicked", {
    tab_name: tabName,
    timestamp: Date.now()
  });
};

export const trackProductColorChanged = (
  catalogId: string,
  fromColorName: string,
  toColorName: string
) => {
  track("design_lab_product_color_changed", {
    catalog_id: catalogId,
    from_color_name: fromColorName,
    to_color_name: toColorName,
    timestamp: Date.now()
  });
};

export const trackProductSizeChanged = (options: {
  catalogNumber: string;
  fromSize: string | null;
  toSize: string | null;
}) => {
  track("design_lab_product_size_changed", {
    catalog_number: options.catalogNumber,
    from_size: options.fromSize,
    to_size: options.toSize,
    timestamp: Date.now()
  });
};

export const trackDesignLabPricingChanged = (options: {
  fromPrice: number | null;
  toPrice: number;
  reason: DesignLabPricingChangeReason;
}) => {
  if (options.fromPrice === null || options.fromPrice === options.toPrice) return;
  track("design_lab_pricing_changed", {
    from_price: options.fromPrice,
    to_price: options.toPrice,
    reason: options.reason,
    timestamp: Date.now()
  });
};

export const trackQuantityChanged = (options: {
  catalogId: string;
  colorName: string;
  size: string;
  quantity: number;
}) => {
  track("design_lab_quantity_changed", {
    catalog_id: options.catalogId,
    color_name: options.colorName,
    size: options.size,
    quantity: options.quantity,
    timestamp: Date.now()
  });
};

export const trackNextChooseSizesClicked = () => {
  track("design_lab_next_choose_sizes_clicked", {
    timestamp: Date.now()
  });
};

export const trackBackButtonClicked = (fromPage: string, toPage: string) => {
  track("design_lab_back_button_clicked", {
    from_page: fromPage,
    to_page: toPage,
    timestamp: Date.now()
  });
};

export const trackProductAddedToDesign = (
  productType: "same_product_different_color" | "different_product",
  catalogId: string,
  colorName: string
) => {
  track("design_lab_product_added", {
    product_type: productType,
    catalog_id: catalogId,
    color_name: colorName,
    timestamp: Date.now()
  });
};

export const trackProductDeletedFromDesign = (
  catalogId: string,
  colorName: string,
  compositionId: string
) => {
  track("design_lab_product_deleted", {
    catalog_id: catalogId,
    color_name: colorName,
    composition_id: compositionId,
    timestamp: Date.now()
  });
};

export const trackDesignLabCtaClicked = (eventName: DesignLabCtaEventName) => {
  track("design_lab_cta_clicked", {
    event_name: eventName,
    timestamp: Date.now()
  });

  // Pilot: CTA Clicked for add to cart
  if (eventName === "add_to_cart") {
    trackRudderstack(
      "Pilot: CTA Clicked",
      {
        cta_type: eventName,
        timestamp: Date.now()
      },
      true
    );
  }
};

export const trackExpertEditAddonToggled = (options: {
  placement: Side;
  artworkId: string;
  composition_ids: string[];
  enabled: boolean;
  paid_expert_edit_addon: boolean | undefined;
  business_line: string;
  catalog_numbers: string[];
  active_config_section: "full_enhancements";
  source: "design_lab";
}) => {
  track("expert_edit_addon_toggled", {
    placement: options.placement,
    artwork_id: options.artworkId,
    composition_ids: options.composition_ids,
    enabled: options.enabled,
    paid_expert_edit_addon: options.paid_expert_edit_addon,
    business_line: options.business_line,
    catalog_numbers: options.catalog_numbers,
    active_config_section: options.active_config_section,
    source: options.source
  });
};

export const trackChangeShirtButtonOpened = () => {
  track("change_shirt_button_opened", {
    timestamp: Date.now()
  });
};

export const trackChangeShirtModalClosed = () => {
  track("change_shirt_modal_closed", {
    timestamp: Date.now()
  });
};

export const trackPriceExposed = (options: {
  priceShown: number;
  component: string;
  step: "PLP" | "DesignLab" | "Subcart" | "Checkout";
  catalogNumber: string | undefined;
  quantity: number;
  businessLine: string;
  unitPrice?: number;
  totalPrice?: number;
  sizeTier?: DesignLabSizeTier;
}) => {
  const augmented: Record<string, unknown> = {
    price_shown: options.priceShown,
    component: options.component,
    step: options.step,
    catalog_number: options.catalogNumber,
    quantity: options.quantity,
    business_line: options.businessLine
  };
  if (options.unitPrice !== undefined) augmented["unit_price"] = options.unitPrice;
  if (options.totalPrice !== undefined) augmented["total_price"] = options.totalPrice;
  if (options.sizeTier !== undefined) augmented["tier"] = options.sizeTier;
  track("price_exposed", augmented);
};
